import * as React from "react"
import { Box } from "@chakra-ui/react"
import parse from "html-react-parser"
import { useRecoilValue } from "recoil"
import { brandingSelector } from "../../../../recoil/atoms/room"
import { ProductDescriptionProps } from "./types"
import styled from "@emotion/styled"
import { cleanProductDescription } from "../../../../util"

type PostionScrollType = "top" | "bottom" | "middle" | "noHasScrollbar"

interface StyledProps {
  positionscroll: PostionScrollType
}

const Container = styled(Box)<StyledProps>`
  --scrollbar-width-product-description: 1px;
  --mask-height-product-description: 20px;
  overflow-y: auto;
  --mask-size-content-product-description: calc(
      100% - var(--scrollbar-width-product-description)
    )
    100%;
  --mask-image-scrollbar-product-description: linear-gradient(black, black);
  --mask-size-scrollbar-product-description: var(
      --scrollbar-width-product-description
    )
    100%;
  mask-image: ${({ positionscroll }) =>
    positionscroll === "noHasScrollbar"
      ? ""
      : `linear-gradient(
    to ${positionscroll === "top" ? "top" : "bottom"},
    transparent,
    black var(--mask-height-product-description),
    black calc(100% - ${
      positionscroll === "middle"
        ? "var(--mask-height-product-description)"
        : "0px"
    }),
    transparent
  ),
  var(--mask-image-scrollbar-product-description)`};
  mask-size: var(--mask-size-content-product-description),
    var(--mask-size-scrollbar-product-description);
  mask-position: 0 0, 100% 0;
  mask-repeat: no-repeat, no-repeat;

  > p {
    margin-bottom: 0.5rem;
  }

  ul:not(.no-margin) {
    margin-left: 20px;
  }

  ol:not(.no-margin) {
    margin-left: 20px;
  }

  a {
    text-decoration: underline;
  }

  h1 {
    font-size: 1.2rem;
    font-weight: 500;
  }

  h2 {
    font-size: 1.1rem;
    font-weight: 500;
  }

  h3 {
    font-size: 1rem;
    font-weight: 400;
  }

  h4 {
    font-size: 0.9rem;
    font-weight: 400;
  }
`

const ProductDescription: React.FC<ProductDescriptionProps> = ({
  description
}) => {
  const roomStyles = useRecoilValue(brandingSelector)
  const descriptionContainerRef = React.useRef<HTMLDivElement>(null)
  const [positionScroll, setPositionScroll] =
    React.useState<PostionScrollType>("top")

  React.useEffect(() => {
    const descriptionContainer = descriptionContainerRef.current
    const observer = new ResizeObserver(entries => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (let entry of entries) {
        if (
          descriptionContainer.scrollHeight < descriptionContainer.clientHeight
        ) {
          setPositionScroll("noHasScrollbar")
        }
      }
    })

    if (descriptionContainer) {
      observer.observe(descriptionContainer)
    }

    return () => {
      if (descriptionContainer) {
        observer.unobserve(descriptionContainer)
      }
    }
  }, [descriptionContainerRef])

  React.useEffect(() => {
    const descriptionContainer = descriptionContainerRef.current

    if (!descriptionContainer) return

    const handleScroll = () => {
      if (descriptionContainer.scrollTop < 10) {
        setPositionScroll("top")
      } else if (
        descriptionContainer?.scrollTop >=
        descriptionContainer?.scrollHeight -
          descriptionContainer?.clientHeight -
          10
      ) {
        setPositionScroll("bottom")
      } else {
        setPositionScroll("middle")
      }
    }

    descriptionContainer.addEventListener("scroll", handleScroll, false)
    return () =>
      descriptionContainer.removeEventListener("scroll", handleScroll, false)
  }, [])

  React.useEffect(() => {
    const links = document.querySelectorAll(".description a")
    links.forEach(link => {
      ;(link as HTMLAnchorElement).target = "_blank"
    })
  }, [])

  const options = {}

  return (
    <Container
      className="description"
      positionscroll={positionScroll}
      ref={descriptionContainerRef}
      overflow="auto"
      fontSize={"0.8rem"}
      height="100%"
      textOverflow="ellipsis"
      color={roomStyles?.badgeTextColor && roomStyles?.badgeTextColor}
    >
      {parse(cleanProductDescription(description), options)}
    </Container>
  )
}

export default ProductDescription
